/**
 * DET SOM ER DELT AV DOKUMENTER, MAPPER OG KARTET (F12, F13; migrering 0661).
 *
 * En delt programpost, vakt eller ressurs åpnes på den vanlige siden for objektet, i gjestemodus. Et
 * dokument, en mappe og kartet har ingen slik side en gjest kan lande på uten å se resten av hylla
 * eller årgangen, så de vises som de er delt — fra definer-funksjoner som bare svarer for mottakeren
 * selv (`app.my_share_content`) eller for tokenet (`app.link_content`). Samme innhold begge steder.
 *
 * ALDRI KONTRAKT ELLER ØKONOMI: en mappe merket som det (eller under en slik) er aldri med, heller
 * ikke som en undermappe av en delt mappe. Og alltid bare lesing.
 *
 * `downloadable` er API-ets svar på skanningen, som i vedleggene: en fil som ikke er skannet eller
 * ikke besto, står i lista men kan ikke lastes ned.
 */
import { z } from 'zod';

/** De delbare tingene som vises her og ikke på en vanlig side. */
export const SHARE_CONTENT_OBJECT_TYPES = ['document', 'folder', 'edition_map'] as const;
export type ShareContentObjectType = (typeof SHARE_CONTENT_OBJECT_TYPES)[number];
export const shareContentObjectTypeSchema = z.enum(SHARE_CONTENT_OBJECT_TYPES);
export function isShareContentType(type: string): type is ShareContentObjectType {
  return (SHARE_CONTENT_OBJECT_TYPES as readonly string[]).includes(type);
}

/** Punktene et delt kart viser: egne punkter (porten, strømmen, vannet), scenene og ressursene. */
export const SHARED_MAP_POINT_TYPES = ['custom', 'stage', 'resource'] as const;

export const sharedFolderSchema = z
  .object({
    id: z.uuid(),
    /** Null for den delte mappa selv. */
    parentId: z.uuid().nullable(),
    name: z.string(),
  })
  .meta({ id: 'SharedFolder' });
export type SharedFolder = z.infer<typeof sharedFolderSchema>;

export const sharedFileSchema = z
  .object({
    id: z.uuid(),
    folderId: z.uuid(),
    filename: z.string(),
    contentType: z.string(),
    byteSize: z.number().int().nonnegative(),
    createdAt: z.iso.datetime(),
    downloadable: z.boolean(),
  })
  .meta({ id: 'SharedFile' });
export type SharedFile = z.infer<typeof sharedFileSchema>;

export const sharedMapPointSchema = z
  .object({
    id: z.uuid(),
    objectType: z.enum(SHARED_MAP_POINT_TYPES),
    label: z.string(),
    secondary: z.string().nullable(),
    latitude: z.string(),
    longitude: z.string(),
    colour: z.string().nullable(),
  })
  .meta({ id: 'SharedMapPoint' });
export type SharedMapPoint = z.infer<typeof sharedMapPointSchema>;

export const sharedMapSchema = z
  .object({
    centerLat: z.string().nullable(),
    centerLng: z.string().nullable(),
    zoom: z.number().int().nullable(),
    hasOverlay: z.boolean(),
    overlayBounds: z
      .object({ south: z.number(), west: z.number(), north: z.number(), east: z.number() })
      .nullable(),
    points: z.array(sharedMapPointSchema),
  })
  .meta({ id: 'SharedMap' });
export type SharedMap = z.infer<typeof sharedMapSchema>;

/** `GET /v1/me/shares/:grantId/content` — det en ekstern med innlogging får se. */
export const sharedContentSchema = z
  .object({
    grantId: z.uuid(),
    objectType: shareContentObjectTypeSchema,
    title: z.string(),
    organizationName: z.string(),
    folders: z.array(sharedFolderSchema),
    files: z.array(sharedFileSchema),
    map: sharedMapSchema.nullable(),
  })
  .meta({ id: 'SharedContent' });
export type SharedContent = z.infer<typeof sharedContentSchema>;
